import { useState, useEffect, useRef } from 'react';
import { App, APP_EVENTS } from '../types/museum/museum';
import { threeJSManager } from '../utils/threeJSManager';

export const useThreeJSApp = (containerRef: React.RefObject<HTMLDivElement>) => {
  const [app, setApp] = useState<App | undefined>(undefined);
  const isMounted = useRef(false);
  const isChangingScene = useRef(false);
  const initializeAttempts = useRef(0);
  const MAX_INITIALIZE_ATTEMPTS = 5;

  useEffect(() => {
    if (!containerRef.current) return;
    isMounted.current = true;

    const clearScene = async () => {
      if (!app?.appEventsBus || !containerRef.current) return;
      app.appEventsBus.emit(APP_EVENTS.CHANGE_SCENE, null);
      await new Promise(r => setTimeout(r, 500));
      containerRef.current.innerHTML = '';
      isChangingScene.current = false;
    };

    const setupEventListeners = (newApp: App) => {
      if (!newApp?.appEventsBus) return;

      newApp.appEventsBus.on(APP_EVENTS.SCENE_READY, async (sceneName) => {
        if (!isMounted.current || sceneName === null) return;
        window.three_app?.restoreCanvas();
        isChangingScene.current = false;
      });

      newApp.appEventsBus.on(APP_EVENTS.CHANGE_SCENE, async (sceneName) => {
        if (!isMounted.current || sceneName === null) return;
        if (!isChangingScene.current) {
          isChangingScene.current = true;
          await clearScene();
          newApp.appEventsBus.emit(APP_EVENTS.CHANGE_SCENE, sceneName);
          window.three_app?.restoreCanvas();
        }
      });
    };

    const initializeApp = async () => {
      if (!isMounted.current || !window.three_app?.start) {
        if (initializeAttempts.current < MAX_INITIALIZE_ATTEMPTS) {
          initializeAttempts.current++;
          setTimeout(initializeApp, 1000);
        }
        return;
      }

      try {
        const appData = await window.three_app.start();
        if (!appData?.app?.appEventsBus) throw new Error('Initialization error');
        setupEventListeners(appData.app);
        setApp(appData.app);
        window.three_app?.restoreCanvas();
      } catch {
        if (initializeAttempts.current < MAX_INITIALIZE_ATTEMPTS) {
          initializeAttempts.current++;
          setTimeout(initializeApp, 1000);
        }
      }
    };

    const startSequence = async () => {
      try {
        initializeAttempts.current = 0;
        await threeJSManager.cleanup();
        await threeJSManager.loadScript('museum');
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (isMounted.current) await initializeApp();
      } catch (error) {
        console.error('Error in Museum start sequence:', error);
      }
    };

    startSequence();

    return () => {
      isMounted.current = false;
      app?.appEventsBus && Object.values(APP_EVENTS).forEach(eventName => {
        app.appEventsBus.removeAllListeners?.(eventName);
      });
    };
  }, [containerRef]);

  return app;
};